const GRID_COLUMNS = 16;
const GRID_ROWS = 9;
const MAX_SAMPLES = 220_000;
const clamp01 = (value) => Math.max(0, Math.min(1, value));
const round = (value, digits = 4) => Number(value.toFixed(digits));
const luma = (r, g, b) => 0.2126 * r + 0.7152 * g + 0.0722 * b;
function isScreenPixel(type, r, g, b) {
    if (type === 'green')
        return g >= 45 && g - Math.max(r, b) >= 32 && g >= r * 1.28 && g >= b * 1.12;
    if (type === 'blue')
        return b >= 45 && b - Math.max(r, g) >= 32 && b >= r * 1.28 && b >= g * 1.1;
    return false;
}
function upstreamMetrics(context) {
    const screen = context.analyzerResults.get('screen-quality');
    const screenMetrics = screen?.metrics;
    const type = screenMetrics?.screen_type;
    const screenType = type === 'green' || type === 'blue' ? type : 'unknown';
    const spill = context.analyzerResults.get('spill-detection');
    const spillMetrics = spill?.metrics;
    const exposure = context.analyzerResults.get('exposure');
    const exposureMetrics = exposure?.metrics;
    const lighting = context.analyzerResults.get('lighting-setup');
    const lightingMetrics = lighting?.metrics;
    const artifacts = context.analyzerResults.get('video-artifacts');
    const artifactMetrics = artifacts?.metrics;
    const exposurePenalty = exposureMetrics
        ? (exposureMetrics.exposure_status === 'too_flat' ? 0.15 : 0) +
            (exposureMetrics.exposure_status === 'too_contrasty' ? 0.08 : 0) +
            (Math.max(exposureMetrics.highlight_clip_percentage ?? 0, exposureMetrics.shadow_clip_percentage ?? 0) > 5 ? 0.1 : 0)
        : 0;
    const lightingPenalty = lightingMetrics
        ? (1 - (lightingMetrics.lighting_balance_score ?? 1)) * 0.15 +
            (lightingMetrics.subject_shadow_on_screen ? 0.1 : 0) +
            ((screenType !== 'unknown' ? 1 - (lightingMetrics.background_uniformity ?? 1) : 0) * 0.1)
        : 0;
    return {
        screenType,
        spillScore: typeof spillMetrics?.spill_score === 'number' ? spillMetrics.spill_score : 0,
        exposurePenalty: clamp01(exposurePenalty),
        lightingPenalty: clamp01(lightingPenalty),
        artifactMetrics,
    };
}
export class EdgeConfidenceAnalyzer {
    id = 'edge-confidence';
    label = 'Edge Confidence';
    analyze(frame, context) {
        const { width, height, data } = frame;
        const stride = Math.max(1, Math.ceil(Math.sqrt((width * height) / MAX_SAMPLES)));
        const { screenType, spillScore, exposurePenalty, lightingPenalty, artifactMetrics } = upstreamMetrics(context);
        let backgroundCount = 0;
        let backgroundR = 0;
        let backgroundG = 0;
        let backgroundB = 0;
        if (screenType !== 'unknown') {
            for (let y = 0; y < height; y += stride) {
                for (let x = 0; x < width; x += stride) {
                    const offset = (y * width + x) * 4;
                    const r = data[offset] ?? 0;
                    const g = data[offset + 1] ?? 0;
                    const b = data[offset + 2] ?? 0;
                    if (!isScreenPixel(screenType, r, g, b))
                        continue;
                    backgroundCount++;
                    backgroundR += r;
                    backgroundG += g;
                    backgroundB += b;
                }
            }
        }
        const meanR = backgroundR / Math.max(1, backgroundCount);
        const meanG = backgroundG / Math.max(1, backgroundCount);
        const meanB = backgroundB / Math.max(1, backgroundCount);
        const backgroundLuma = luma(meanR, meanG, meanB);
        const neighborDistance = Math.max(1, stride * 2);
        const neighbors = [[-neighborDistance, 0], [neighborDistance, 0], [0, -neighborDistance], [0, neighborDistance]];
        const cellConfidence = new Array(GRID_COLUMNS * GRID_ROWS).fill(0);
        const cellCount = new Array(GRID_COLUMNS * GRID_ROWS).fill(0);
        let edgeCount = 0;
        let weakCount = 0;
        let softCount = 0;
        let confidenceSum = 0;
        let separationSum = 0;
        if (screenType !== 'unknown' && backgroundCount > 0) {
            for (let y = 0; y < height; y += stride) {
                for (let x = 0; x < width; x += stride) {
                    const offset = (y * width + x) * 4;
                    const r = data[offset] ?? 0;
                    const g = data[offset + 1] ?? 0;
                    const b = data[offset + 2] ?? 0;
                    if (isScreenPixel(screenType, r, g, b))
                        continue;
                    let nearestScreen;
                    for (const [dx, dy] of neighbors) {
                        const nx = x + dx;
                        const ny = y + dy;
                        if (nx < 0 || nx >= width || ny < 0 || ny >= height)
                            continue;
                        const neighborOffset = (ny * width + nx) * 4;
                        const nr = data[neighborOffset] ?? 0;
                        const ng = data[neighborOffset + 1] ?? 0;
                        const nb = data[neighborOffset + 2] ?? 0;
                        if (isScreenPixel(screenType, nr, ng, nb)) {
                            nearestScreen = [nr, ng, nb];
                            break;
                        }
                    }
                    if (!nearestScreen)
                        continue;
                    const chromaDistance = Math.hypot(r - meanR, g - meanG, b - meanB);
                    const chromaSeparation = clamp01(chromaDistance / 190);
                    const lumaContrast = clamp01(Math.abs(luma(r, g, b) - backgroundLuma) / 100);
                    const localGradient = clamp01(Math.hypot(r - nearestScreen[0], g - nearestScreen[1], b - nearestScreen[2]) / 190);
                    const pixelConfidence = clamp01(chromaSeparation * 0.55 + lumaContrast * 0.25 + localGradient * 0.2);
                    const isWeak = pixelConfidence < 0.4;
                    const isSoft = localGradient < 0.36 || (lumaContrast < 0.12 && chromaSeparation < 0.5);
                    edgeCount++;
                    if (isWeak)
                        weakCount++;
                    if (isSoft)
                        softCount++;
                    confidenceSum += pixelConfidence;
                    separationSum += chromaSeparation;
                    const column = Math.min(GRID_COLUMNS - 1, Math.floor((x / width) * GRID_COLUMNS));
                    const row = Math.min(GRID_ROWS - 1, Math.floor((y / height) * GRID_ROWS));
                    const cellIndex = row * GRID_COLUMNS + column;
                    cellConfidence[cellIndex] = (cellConfidence[cellIndex] ?? 0) + pixelConfidence;
                    cellCount[cellIndex] = (cellCount[cellIndex] ?? 0) + 1;
                }
            }
        }
        const rawConfidence = edgeCount ? confidenceSum / edgeCount : 0;
        const rawEdgeConfidenceScore = clamp01(rawConfidence * (1 - spillScore * 0.35) * (1 - exposurePenalty) * (1 - lightingPenalty));
        const weakPercentage = weakCount / Math.max(1, edgeCount) * 100;
        const softPercentage = softCount / Math.max(1, edgeCount) * 100;
        const rawChromaSeparationScore = separationSum / Math.max(1, edgeCount);
        const artifactImpactReasons = [];
        const macroblocking = artifactMetrics?.macroblocking_score ?? 0;
        const artifactMotionBlur = artifactMetrics?.motion_blur_score ?? 0;
        const artifactNoise = artifactMetrics?.noise_score ?? 0;
        const oversharpening = artifactMetrics?.oversharpening_score ?? 0;
        const interlacing = artifactMetrics?.interlacing_score ?? 0;
        const flicker = artifactMetrics?.flicker_score ?? 0;
        if (macroblocking >= 0.18)
            artifactImpactReasons.push('Compression artifacts detected around edges');
        if (artifactMotionBlur >= 0.3)
            artifactImpactReasons.push('Motion blur may soften subject boundary');
        if (artifactNoise >= 0.2)
            artifactImpactReasons.push('Chroma noise reduces chroma separation reliability');
        if (oversharpening >= 0.2)
            artifactImpactReasons.push('Over-sharpening halos reduce edge cleanliness');
        if (interlacing >= 0.2)
            artifactImpactReasons.push('Interlacing combing reduces edge stability');
        if (flicker >= 0.2)
            artifactImpactReasons.push('Temporal flicker reduces edge consistency');
        const artifactPenaltyScore = clamp01(macroblocking * 0.16 + artifactMotionBlur * 0.18 + artifactNoise * 0.12 +
            oversharpening * 0.12 + interlacing * 0.14 + flicker * 0.08);
        const finalEdgeConfidenceScore = clamp01(rawEdgeConfidenceScore - artifactPenaltyScore);
        const chromaSeparationScore = clamp01(rawChromaSeparationScore * (1 - artifactNoise * 0.25));
        const motionBlurRisk = edgeCount === 0 ? 'unknown'
            : softPercentage >= 45 ? 'high' : softPercentage >= 20 ? 'medium' : 'low';
        const warnings = [];
        const addWarning = (code, message) => {
            warnings.push({ code, severity: 'warning', message, module_id: this.id, frame_number: context.frameNumber });
        };
        if (edgeCount === 0 || weakPercentage >= 20 || finalEdgeConfidenceScore < 0.4)
            addWarning('weak_edge_separation', 'Weak edge separation detected');
        if (edgeCount > 0 && chromaSeparationScore < 0.38)
            addWarning('subject_near_background_color', 'Subject color too close to background');
        if (softPercentage >= 20)
            addWarning('soft_edges', 'Soft edges may produce keying artifacts');
        if (motionBlurRisk === 'medium' || motionBlurRisk === 'high')
            addWarning('motion_blur_edge', 'Motion blur detected near edge');
        if (spillScore >= 0.2)
            addWarning('spill_reduces_confidence', 'Spill is reducing edge confidence');
        if (artifactPenaltyScore >= 0.03)
            addWarning('artifacts_reduce_edge_confidence', 'Video artifacts are reducing edge confidence');
        if (macroblocking >= 0.18)
            addWarning('compression_matte_chatter', 'Compression around edges may create matte chatter');
        if (artifactMotionBlur >= 0.3)
            addWarning('artifact_motion_blur', 'Motion blur may reduce key quality');
        const overlays = [];
        cellCount.forEach((count, index) => {
            if (!count)
                return;
            const confidence = (cellConfidence[index] ?? 0) / count;
            const column = index % GRID_COLUMNS;
            const row = Math.floor(index / GRID_COLUMNS);
            const points = [
                { x: column / GRID_COLUMNS, y: row / GRID_ROWS },
                { x: (column + 1) / GRID_COLUMNS, y: (row + 1) / GRID_ROWS },
            ];
            overlays.push({
                type: 'heatmap', points, label: 'Edge confidence heatmap',
                color: confidence < 0.4 ? '#ff4d6d' : confidence < 0.7 ? '#ffd166' : '#60e68a',
                opacity: 0.55, stroke_width: 1, fill: true,
            });
            if (confidence < 0.4) {
                overlays.push({
                    type: 'box', points, label: 'Weak edge zone',
                    color: '#ff3355', opacity: 0.9, stroke_width: 2,
                });
            }
        });
        return {
            module_id: this.id,
            module_name: this.label,
            confidence: edgeCount === 0 ? 0.2 : round(clamp01(0.6 + Math.min(0.4, edgeCount / 1000))),
            frame_number: context.frameNumber,
            timestamp_ms: context.timestampMs,
            metrics: {
                edge_confidence_score: round(finalEdgeConfidenceScore),
                raw_edge_confidence_score: round(rawEdgeConfidenceScore),
                artifact_penalty_score: round(artifactPenaltyScore),
                final_edge_confidence_score: round(finalEdgeConfidenceScore),
                artifact_impact_reasons: artifactImpactReasons,
                weak_edge_percentage: round(weakPercentage, 2),
                soft_edge_percentage: round(softPercentage, 2),
                chroma_separation_score: round(chromaSeparationScore),
                motion_blur_risk: motionBlurRisk,
            },
            warnings,
            overlays,
        };
    }
}
//# sourceMappingURL=EdgeConfidenceAnalyzer.js.map